import React, { memo, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";

import { DecimalInput } from "../../../../components";

import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectProfitCalculator } from "../../../../features/productAnalyzer/productAnalyzer.selector";
import Calculator from "../../../../helpers/profitCalculator";
import { formatCurrency, toNumber } from "../../../../helpers";
import { TableRow, TableCell } from "./index.styles";
import { selectProductAnalyzerSettings } from "../../../../features/settings/settings.selector";
import { ROI_PERCENTS } from "../../../../constants";
import { updateLookupThunk } from "../../../../features/productAnalyzer/productAnalyzer.thunk";

const Roi: React.FC = () => {
  const dispatch = useAppDispatch();
  const { profitCalculator, feeRules } = useAppSelector(selectProfitCalculator);
  const { defaultCustomRoiCalc } = useAppSelector(
    selectProductAnalyzerSettings,
  );

  const [roi, setRoi] = useState<number>(defaultCustomRoiCalc);
  const [profit, setProfit] = useState<number>();
  const [salePrice, setSalePrice] = useState<number | undefined>();

  const calculator = useMemo(
    () => new Calculator(profitCalculator, feeRules),
    [profitCalculator, feeRules],
  );

  useEffect(() => {
    setProfit(calculator.calculateProfitFromRoi(roi));
    setSalePrice(calculator.calculateSalePriceFromRoi(roi));
  }, [calculator]);

  const handleRoiChange = (value: number) => {
    setRoi(value);
    setProfit(calculator.calculateProfitFromRoi(value));
    setSalePrice(calculator.calculateSalePriceFromRoi(value));
  };

  const handleProfitChange = (value: number) => {
    setProfit(value);
    setRoi(calculator.calculateRoiFromProfit(value));
    setSalePrice(calculator.calculateSalePriceFromProfit(value));
  };

  return (
    <Stack>
      <Box
        display={"grid"}
        gridTemplateColumns={"1fr 4fr 4fr 4fr"}
        alignItems={"center"}
        justifyItems={"center"}
        sx={{
          background: "#FFFFFFF7",
          height: "32px",
          color: "#000",
          borderBottom: "1px solid #dcdddd",
        }}
      >
        <Box></Box>
        <Typography variant="subtitle1">R.O.I.</Typography>
        <Typography variant="subtitle1">Profit</Typography>
        <Typography variant="subtitle1">Sale Price</Typography>
      </Box>
      <Box
        display={"grid"}
        gridTemplateColumns={"1fr 4fr 4fr 4fr"}
        alignItems={"center"}
        sx={{
          height: "43px",
          borderBottom: "1px solid #dcdddd",
          backgroundColor: "#fff",
        }}
      >
        <Box></Box>
        <Box p={0.5}>
          <DecimalInput
            suffix="%"
            value={roi}
            align="center"
            size="md"
            onEditing={handleRoiChange}
          />
        </Box>
        <Box p={0.5}>
          <DecimalInput
            prefix="$"
            value={profit}
            align="center"
            size="md"
            onEditing={handleProfitChange}
          />
        </Box>
        <Box p={0.5}>
          <DecimalInput
            type={salePrice === undefined ? "string" : "number"}
            prefix="$"
            readOnly={true}
            value={salePrice === undefined ? "-" : salePrice}
            align="center"
            size="md"
            disabled
          />
        </Box>
      </Box>
      <Box display={"flex"} width={"100%"} flexDirection={"column"}>
        {ROI_PERCENTS.map((percent, index) => {
          const calculatedPrice = calculator.calculateSalePriceFromRoi(
            percent * 100,
          );
          return (
            <TableRow key={index}>
              <TableCell
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {index + 1}
              </TableCell>
              <TableCell>{percent * 100}%</TableCell>
              <TableCell>
                {formatCurrency(
                  calculator.calculateProfitFromRoi(percent * 100),
                )}
              </TableCell>
              <TableCell>
                <Box
                  sx={{ cursor: "copy !important" }}
                  onClick={() =>
                    dispatch(
                      updateLookupThunk({
                        salePrice: calculatedPrice,
                      }),
                    )
                  }
                >
                  {formatCurrency(calculatedPrice)}
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </Box>
      <Box
        height={"32px"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Typography
          sx={{
            color: "#000",
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          Based on your cost price of&nbsp;
          {formatCurrency(profitCalculator.costPrice)}
        </Typography>
      </Box>
    </Stack>
  );
};

export default memo(Roi);
